import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './LabData.module.css'
import {
  FREQ_SCALE,
  ATTEN_SCALE,
  ATTEN_BLURB_TAIL,
  tabMeasure,
  freqX,
  freqFromX,
  hzText,
  nearestPoint,
  tablePoints,
  LAB_PEER_COLORS,
  LAB_PRIMARY_COLOR,
  LAB_TARGET_COLOR,
  PLOT_X,
  type ChartGeometry,
  FREQ_TARGET_POINTS,
  FREQ_TARGET_NAME,
  FREQ_BLURB_PARTS,
  FREQ_CURVE_LINK_HREF,
  type LabChartPaths,
  type AxisScale,
  type AxisTick,
  type LabMeasure,
  type LabTabKey,
  type Point,
} from 'lib/dealhunt/audioChart'
import { MDAQS_EXPLAINER_URL, MOS_EXPLAINER_URL } from 'lib/dealhunt/constants'
import type { MdaqsScore } from 'lib/dealhunt/review'
import type { DealHuntMdaqs } from 'lib/dealhunt/types'

/**
 * The lab charts themselves — tabs, the two line charts with their hover crosshair and
 * readout, the MDAQS bars, legends, blurbs and (optionally) the crawler tables — with no
 * opinion about where the overlays come from. The product page's `LabData` section wraps it
 * with the peer picker; the category page's compare dialog and pinned drawer render it
 * against the reader's declared pair (2026-09-08 ticket "Lab Charts Off the Card").
 */

/** One overlaid product. `curves` is `undefined` while loading, `null` when the lab has none. */
export interface PanelOverlay {
  key: string
  name: string
  color: string
  curves: LabChartPaths | null | undefined
  mdaqs: DealHuntMdaqs | null
  /** Deselected and fading out: still drawn, no longer in the legend or the MDAQS bars. */
  leaving?: boolean
}

/**
 * A frequency band to call out on one chart: a tinted column from `lo` to `hi` and the gap
 * between the primary curve and the `against` overlay filled inside it — the region the
 * "+6 dB bass" sentence averages, drawn where it comes from.
 */
export interface BandHighlight {
  measure: LabMeasure
  lo: number
  hi: number
  text: string
  /** Second line under the text, e.g. "20–250 Hz, averaged". */
  detail?: string
  /** Key of the overlay the gap is filled against; the first overlay with curves when absent. */
  against?: string
}

export interface LabTab {
  key: LabTabKey
  label: string
}

/** The tabs a panel renders, in order — the product page reads the first for its initial tab. */
export function labTabs(curves: LabChartPaths, hasMdaqs: boolean): LabTab[] {
  return labTabsFor(measuresOf(curves), hasMdaqs)
}

/** The measures a set of curves carries. */
export function measuresOf(curves: LabChartPaths | null | undefined): Set<LabMeasure> {
  const set = new Set<LabMeasure>()
  if (curves?.frequency) {
    set.add('frequency')
  }
  if (curves?.isolation) {
    set.add('isolation')
  }
  if (curves?.anc) {
    set.add('anc')
  }
  return set
}

/** The measures drawn by a panel: the primary's, plus every overlay's when `tabsFrom` is
 *  `'any'` (the compare drawer, 2026-10-18 — the first pin set the tabs for every pin after it:
 *  a frequency-only primary hid the Isolation and Noise cancelling tabs the other pins had). */
export function measuresDrawn(
  curves: LabChartPaths,
  overlays: ReadonlyArray<Pick<PanelOverlay, 'curves'>>,
  tabsFrom: 'primary' | 'any'
): Set<LabMeasure> {
  const set = measuresOf(curves)
  if (tabsFrom === 'any') {
    for (const o of overlays) {
      for (const mk of measuresOf(o.curves)) {
        set.add(mk)
      }
    }
  }
  return set
}

export function labTabsFor(measures: ReadonlySet<LabMeasure>, hasMdaqs: boolean): LabTab[] {
  const tabs: Array<LabTab | null> = [
    measures.has('frequency') ? { key: 'freq', label: 'Frequency response' } : null,
    measures.has('isolation') || measures.has('anc')
      ? { key: 'atten', label: 'Isolation & ANC' }
      : null,
    hasMdaqs ? { key: 'mdaqs', label: 'Audio quality' } : null,
  ]
  return tabs.filter((t): t is LabTab => t !== null)
}

/** `active` when it is one of `tabs`, else the first tab (frequency response when there is one).
 *  Switching to a product without the active tab's data, or unpinning it, takes the tab away;
 *  the reader's choice stays held, so it comes back with a product that has it. */
export const shownTab = (tabs: ReadonlyArray<LabTab>, active: LabTabKey): LabTabKey =>
  tabs.some(t => t.key === active) ? active : tabs[0]?.key ?? active

interface LabChartPanelProps {
  /** Display name of the primary product (legend / bar labels / aria). */
  productName: string
  curves: LabChartPaths
  mdaqs: MdaqsScore[]
  /** The Audio quality tab is expected but its scores are still on their way. */
  mdaqsPending?: boolean
  /** The primary curve's colour — SoundGuys blue on the product page. */
  primaryColor?: string
  overlays: PanelOverlay[]
  active: LabTabKey
  onTabClick: (tab: LabTab) => void
  geom: ChartGeometry
  /** Prefix for the tab/panel element ids — two panels on one page must not share them. */
  idPrefix?: string
  /** Rendered between the tab strip and the panels (the product page's peer picker). */
  toolbar?: React.ReactNode
  /** The visually hidden measurement tables (SEO) — the product page only. */
  tables?: boolean
  highlight?: BandHighlight | null
  /** Key of the overlay the hover readout subtracts from the primary curve, as a trailing row
   *  ("Difference · this minus yours") — the quantity the band chips average. */
  hoverDiff?: string
  className?: string
  /** False: the caller draws its own key (the compare drawer's pinned list, 2026-09-09). */
  legend?: boolean
  /** False: no explanatory paragraph under the charts (the drawer: the chart is the object). */
  blurbs?: boolean
  /** Which curves decide the tabs. `primary` (the default, the product page): the product's
   *  own measures — a peer never adds a chart the product has no data for. `any` (the compare
   *  drawer): every measure any pinned curve carries; a tab whose primary has no curve draws
   *  the overlays alone, with no blurb, table or area fill for the missing primary. */
  tabsFrom?: 'primary' | 'any'
  /** Highlighted lines, for a host that draws its own key (`legend={false}`) and lets its rows
   *  toggle them: keys are `PRIMARY_KEY`, `TARGET_KEY` and overlay keys. Uncontrolled when absent. */
  focus?: ReadonlySet<string>
  onFocusChange?: (next: ReadonlySet<string>) => void
}

/** The four MDAQS metrics, in display order, keyed to both data shapes. */
const MDAQS_METRICS = [
  { key: 'timbre', label: 'Timbre' },
  // Soft hyphens: a narrow chart (the compare drawer) breaks these at a syllable, not mid-letter.
  { key: 'distortion', label: 'Dis­tor­tion' },
  { key: 'immersiveness', label: 'Immer­sive­ness' },
  { key: 'overall', label: 'Overall' },
] as const

/** MDAQS bars stand on the bottom of the 1–5 opinion scale, like the internal tool's chart. */
const MOS_MIN = 1
const MOS_MAX = 5
const MOS_TICKS = [1, 2, 3, 4, 5]
const mosPct = (score: number): number =>
  Math.min(100, Math.max(0, ((score - MOS_MIN) / (MOS_MAX - MOS_MIN)) * 100))

/** The two attenuation measures, drawn on one chart: isolation solid, ANC dashed. */
const ATTEN_MEASURES = [
  { measure: 'isolation', label: 'isolation', key: 'Isolation (ANC off)', dashed: false },
  { measure: 'anc', label: 'ANC', key: 'ANC on', dashed: true },
] as const

/**
 * The ANC line's dash per product, by palette slot (the primary first, then `LAB_PEER_COLORS`
 * order) so a product keeps its pattern as peers come and go. With two or more products on the
 * attenuation chart, the patterns tell the ANC lines apart where the colours crowd together.
 */
const ANC_DASHES = ['8 5', '1 5', '12 4 2 4', '18 5', '10 3 2 3 2 3']
const ancDash = (color: string): string =>
  ANC_DASHES[LAB_PEER_COLORS.indexOf(color) + 1] ?? ANC_DASHES[0]

const TARGET_DASH = '10 5'
/** The preference curve is a reference, so it sits back from the measured products. 0.65 is the
 *  lowest that keeps it at 3:1 on the frame (WCAG 1.4.11; 0.6 measured 2.87). */
const TARGET_OPACITY = 0.65

/** Legend keys for the two lines that aren't overlays (overlay keys are product keys). */
export const PRIMARY_KEY = ':primary'
export const TARGET_KEY = ':target'
/** `keys` with `key` added, or removed if it was there. */
export const toggleKey = (keys: ReadonlySet<string>, key: string): ReadonlySet<string> => {
  const next = new Set(keys)
  if (!next.delete(key)) {
    next.add(key)
  }
  return next
}
const measureKey = (m: LabMeasure): string => `:${m}`

/** With lines highlighted, every other line keeps this much of its saturation and opacity. */
const DIM_SATURATION = 0.2
const DIM_OPACITY = 0.4
const desaturate = (hex: string): string => {
  const rgb = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16))
  const grey = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]
  const [r, g, b] = rgb.map(c => Math.round(grey + (c - grey) * DIM_SATURATION))
  return `rgb(${r}, ${g}, ${b})`
}
const paint = (color: string, dimmed: boolean): string => (dimmed ? desaturate(color) : color)
/** Click target around a line, in viewBox units (~12px at desktop width). */
const LINE_HIT_WIDTH = 10

/** A chart's highlight: whether a legend entry, or a drawn line, is greyed out. */
interface ChartFocus {
  item: (key: string) => boolean
  line: (key: string, measure: LabMeasure) => boolean
}

/** One drawn curve. `key` is its legend entry (a product or the target). */
interface ChartLine {
  key: string
  measure: LabMeasure
  d: string
  color: string
  width: number
  dash?: string
  opacity?: number
  /** An overlay: draws on and fades out. The primary and the target are static. */
  peer?: boolean
  leaving?: boolean
}

/** Gutter between the y-axis labels and the plot's left edge. */
const Y_LABEL_GAP = 6
/** Gap between the plot's bottom edge and the frequency labels' baseline. */
const X_LABEL_GAP = 18
/** Glyph advance of the monospace tick labels, in em. */
const MONO_EM = 0.6

/**
 * Machine-readable companion to each SVG chart: the measured series as a real
 * `<table>`, log-decimated to ~31 rows (see `tablePoints`). The SVG is pixels to a
 * crawler — this table is what makes the lab data (the dataset nobody else has)
 * legible to search engines and AI agents (SEO audit C2). Visually hidden, not removed:
 * sighted users get the same data from the chart itself, while screen readers and crawlers
 * read the table — the sr-only-alternative-for-a-chart pattern, not hidden-text stuffing.
 */
const MeasurementTable: React.VFC<{
  measurement: string
  productName: string
  points: Point[]
  dbColumn: string
}> = ({ measurement, productName, points, dbColumn }) => (
  <div className={styles.dataTable}>
    <table>
      <caption>{`${productName}: ${measurement.toLowerCase()}, as measured in the SoundGuys lab. Frequency in hertz.`}</caption>
      <thead>
        <tr>
          <th scope="col">{'Frequency (Hz)'}</th>
          <th scope="col">{dbColumn}</th>
        </tr>
      </thead>
      <tbody>
        {tablePoints(points).map(([hz, db]) => (
          <tr key={hz}>
            <td>{Math.round(hz)}</td>
            <td>{db.toFixed(1)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

/** One hoverable curve on a chart: the primary product, the target, or an overlay peer. */
interface HoverSeries {
  name: string
  color: string
  points: Point[]
  /** ANC on the attenuation chart: same colour as its isolation line, so the mobile strip
   *  (which drops names) tells them apart by a hollow swatch. */
  dashed?: boolean
}

const hoverDbLabel = (db: number, signed: boolean): string =>
  `${signed && db > 0 ? '+' : ''}${db.toFixed(1)} dB`

/**
 * Hover crosshair: a hairline at the cursor's frequency plus a pin on each visible curve at
 * that curve's nearest measured sample (log distance — never an interpolated value, the same
 * rule as the data tables). Values are read out by `HoverReadout`; both layers are aria-hidden
 * because the measurement tables already carry the exact numbers for assistive tech.
 */
const ChartCrosshair: React.VFC<{
  hz: number
  series: HoverSeries[]
  scale: AxisScale
  geom: ChartGeometry
}> = ({ hz, series, scale, geom }) => (
  <g aria-hidden="true" pointerEvents="none">
    <line
      x1={freqX(hz)}
      y1={geom.plot.padT}
      x2={freqX(hz)}
      y2={geom.plot.padT + geom.plot.plotH}
      className={styles.crosshairLine}
    />
    {series.map(s => {
      const [hzNear, db] = nearestPoint(s.points, hz)
      return (
        <circle
          key={s.name}
          cx={freqX(hzNear)}
          cy={geom.scaleY(scale, db)}
          r={geom.plot.pinR}
          fill={s.color}
          className={styles.crosshairPin}
        />
      )
    })}
  </g>
)

/** Rig named in every line-chart title, as the internal measurement tool does. */
const RIG_CONTEXT = '(measured on a B&K 5128)'

/**
 * Chart title in the internal tool's `product descriptor context` form. Each part is an
 * unbreakable inline block, so ordinary line breaking reproduces the tool's wrap order — then
 * `product descriptor` / `context`, then `product` / `descriptor context`, then three lines —
 * without ever splitting the product name or the context mid-phrase.
 */
const ChartTitle: React.VFC<{ product: string; descriptor: string; context?: string }> = ({
  product,
  descriptor,
  context,
}) => (
  <p className={styles.chartTitle}>
    <span>{product}</span> <span>{descriptor}</span>
    {context && (
      <>
        {' '}
        <span>{context}</span>
      </>
    )}
  </p>
)

/** Idle-state affordance for the mobile readout strip (desktop hides the strip entirely). */
const HOVER_HINT = 'Tap the chart to read values'

/**
 * The value readout for the crosshair, rendered in two shapes from one DOM (the split is
 * CSS-only, see `.hoverTip`):
 *
 * - desktop — a floating box beside the cursor, anchored by percentage of the wrapper (the SVG
 *   scales with its container, so viewBox-x / viewW is the correct fraction at any width) and
 *   flipped left of the cursor past ~60% so it never overhangs the right edge.
 * - mobile — a fixed strip ABOVE the chart. A finger-following box is unusable on a ~330px
 *   plot: it covers the curve whichever way it flips, and the finger covers the box. The strip
 *   keeps its height whether idle or active, so the chart never shifts under the thumb, and
 *   drops the series names (the legend directly above already maps color → product).
 *
 * `hz === null` renders the idle strip rather than nothing — that reserved space IS the
 * no-layout-shift guarantee. The headline frequency is the primary curve's nearest real sample.
 */
const HoverReadout: React.VFC<{
  hz: number | null
  series: HoverSeries[]
  signed: boolean
  /** Name of the series to subtract from the primary (series[0]) in a trailing difference row. */
  diffWith?: string
}> = ({ hz, series, signed, diffWith }) => {
  if (hz === null || !series.length) {
    return (
      <div className={styles.hoverTip} data-idle="" aria-hidden="true">
        <span className={styles.hoverTipHint}>{HOVER_HINT}</span>
      </div>
    )
  }
  const against = diffWith ? series.find((s, i) => i > 0 && s.name === diffWith) ?? null : null
  const leftPct = (freqX(hz) / PLOT_X.viewW) * 100
  return (
    <div
      className={styles.hoverTip}
      data-flip={freqX(hz) > PLOT_X.padL + PLOT_X.plotW * 0.6 ? '' : undefined}
      // `--tip-left` is the anchor as a percentage, so the CSS can cap the tip at the room
      // actually left beside it. Without the cap a wide readout (the frequency tab carries the
      // preference curve, so it is the widest) runs past the panel, and in a floating drawer
      // that overflow makes the panel scrollable — which resizes the chart, which moves the
      // pointer off it, which hides the tip. The chart appears to zoom in and out.
      style={{ left: `${leftPct}%`, '--tip-left': `${leftPct}%` } as React.CSSProperties}
      aria-hidden="true"
    >
      <span className={styles.hoverTipHz}>{hzText(nearestPoint(series[0].points, hz)[0])}</span>
      {series.map(s => (
        <span key={s.name} className={styles.hoverTipRow}>
          <span
            className={s.dashed ? styles.hoverTipSwatchHollow : styles.hoverTipSwatch}
            style={s.dashed ? { borderColor: s.color } : { background: s.color }}
          />
          <span className={styles.hoverTipName}>{s.name}</span>
          <span className={styles.hoverTipVal}>
            {hoverDbLabel(nearestPoint(s.points, hz)[1], signed)}
          </span>
        </span>
      ))}
      {against && (
        <span className={`${styles.hoverTipRow} ${styles.hoverTipDiff}`}>
          <span className={styles.hoverTipDiffName}>{'Difference · this minus yours'}</span>
          <span className={styles.hoverTipVal}>
            {hoverDbLabel(
              nearestPoint(series[0].points, hz)[1] - nearestPoint(against.points, hz)[1],
              true
            )}
          </span>
        </span>
      )}
    </div>
  )
}

/**
 * Frequency axis labels, positioned by the same `freqX` log scale that draws the curves — never
 * by hand-measured pixels, or a geometry change desyncs the two. The last label is pulled inside
 * the frame so "20k" doesn't overhang the right edge (it's centered on the plot's final pixel).
 */
const EDGE_INSET = 4
const FREQ_X_TICKS: Array<[hz: number, label: string]> = [
  [20, '20'],
  [100, '100'],
  [1000, '1k'],
  [10000, '10k'],
  [20000, '20k'],
]
const FREQ_X_LABELS = FREQ_X_TICKS.map(([hz, label], i) => ({
  label,
  x: freqX(hz) - (i === FREQ_X_TICKS.length - 1 ? EDGE_INSET : 0),
}))

/**
 * Vertical gridlines for the log frequency axis, positioned by `freqX` like everything else —
 * the log10 mapping is why the minors bunch toward each decade's end (30…90 spread wide,
 * 60…90 crowd together), which is the correct spacing for a log axis, not a bug. Majors sit
 * on the decade boundaries; the 20 Hz / 20 kHz plot edges are skipped (they're the frame).
 */
const FREQ_MAJOR_GRID_X = [100, 1000, 10000].map(freqX)
const FREQ_MINOR_GRID_X = [3, 4, 5, 6, 7, 8, 9]
  .flatMap(m => [m * 10, m * 100, m * 1000])
  .concat([200, 2000])
  .map(freqX)

/**
 * Shared SVG scaffolding (gridlines + axis labels) for the line charts. Only the vertical
 * extents and the label size come from `geom` — the gridline x-positions are profile-free
 * (see `PlotProfile`), so they stay module constants.
 */
const ChartAxes: React.VFC<{ yTicks: AxisTick[]; yTitle: string; geom: ChartGeometry }> = ({
  yTicks,
  yTitle,
  geom,
}) => {
  const { padL, padT, plotW, plotH, labelSize } = geom.plot
  const midY = padT + plotH / 2
  // The svg starts at the frame's edge (`.chartFrameBleed`), so centring the title between x=0
  // and the widest tick label keeps edge→title and title→ticks equal at any rendered width.
  const tickLeft =
    padL - Y_LABEL_GAP - Math.max(...yTicks.map(t => t.label.length)) * MONO_EM * labelSize
  const titleX = tickLeft / 2
  return (
    <>
      <g className={styles.minorGrid}>
        {FREQ_MINOR_GRID_X.map(x => (
          <line key={x} x1={x} y1={padT} x2={x} y2={padT + plotH} />
        ))}
      </g>
      <g className={styles.majorGrid}>
        {FREQ_MAJOR_GRID_X.map(x => (
          <line key={x} x1={x} y1={padT} x2={x} y2={padT + plotH} />
        ))}
      </g>
      <g className={styles.yGrid}>
        {yTicks.map(t => (
          <line key={t.label} x1={padL} y1={t.y} x2={padL + plotW} y2={t.y} />
        ))}
      </g>
      <g className={styles.axisLabel} fontSize={labelSize} fontFamily="monospace" textAnchor="end">
        {yTicks.map(t => (
          <text key={t.label} x={padL - Y_LABEL_GAP} y={t.y + labelSize * 0.4}>
            {t.label}
          </text>
        ))}
      </g>
      <g
        className={styles.axisLabel}
        fontSize={labelSize}
        fontFamily="monospace"
        textAnchor="middle"
      >
        {FREQ_X_LABELS.map(t => (
          <text key={t.label} x={t.x} y={padT + plotH + X_LABEL_GAP}>
            {t.label}
          </text>
        ))}
      </g>
      <g className={styles.axisTitle} fontSize={labelSize} textAnchor="middle">
        <text
          x={titleX}
          y={midY}
          dominantBaseline="central"
          transform={`rotate(-90 ${titleX} ${midY})`}
        >
          {yTitle}
        </text>
        <text x={padL + plotW / 2} y={padT + plotH + X_LABEL_GAP + labelSize * 1.5}>
          {'Frequency (Hz)'}
        </text>
      </g>
    </>
  )
}

/**
 * One curve (no area fill). An animated overlay gets `pathLength={1}`, which normalizes the path
 * so the CSS dash-offset trick draws it left-to-right on mount; `leaving` swaps to a fade-out. A
 * dashed line only fades in: the draw trick IS a dasharray, so it would erase the dashes.
 */
const ChartPath: React.VFC<{
  line: ChartLine
  dimmed: boolean
  hidden?: boolean
  still?: boolean
}> = ({ line, dimmed, hidden = false, still = false }) => {
  const animate = line.peer && !still
  return (
    <path
      d={line.d}
      fill="none"
      stroke={paint(line.color, dimmed)}
      strokeOpacity={(line.opacity ?? 1) * (dimmed ? DIM_OPACITY : 1)}
      strokeWidth={line.width}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={line.dash}
      pathLength={animate && !line.dash ? 1 : undefined}
      visibility={hidden ? 'hidden' : undefined}
      className={
        animate
          ? clsx(line.dash ? styles.chartLineDashed : styles.chartLine, {
              [styles.leaving]: line.leaving,
            })
          : undefined
      }
    />
  )
}

/**
 * How to highlight, under each line chart's frame. Line clicks are mouse-only (`renderLines`), so
 * a touch screen gets the legend half, or nothing where the caller draws its own key.
 */
const HighlightHint: React.VFC<{ legend: boolean }> = ({ legend }) => (
  <p className={styles.chartHint} data-pointer-only={!legend || undefined}>
    <span className={styles.hintPointer}>
      {legend
        ? 'Click a line or a legend entry to highlight it. Click again to clear.'
        : 'Click a line to highlight it. Click again to clear.'}
    </span>
    {legend && (
      <span className={styles.hintTouch}>
        {'Tap a legend entry to highlight its line. Tap again to clear.'}
      </span>
    )}
  </p>
)

/** A legend swatch drawn as the line itself, for the per-product ANC dashes. */
const LineSwatch: React.VFC<{ color: string; dash: string }> = ({ color, dash }) => (
  <svg className={styles.legendSwatch} viewBox="0 0 28 4" aria-hidden="true">
    <line
      x1="1"
      y1="2"
      x2="27"
      y2="2"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeDasharray={dash}
    />
  </svg>
)

/** Average glyph advance of the UI face, in em — measured off the rendered callout (~0.5) and
 *  rounded up, so the estimate errs towards keeping the sentence inside the frame. */
const CHAR_EM = 0.52

/** Samples across the sentence's own span when checking which curves run through it. */
const LABEL_PROBES = 16

/**
 * The band callout: a tinted column over `lo…hi` and the sentence on the column's top edge — or
 * its bottom edge when the curves run through the top one. No fill between the curves: outside
 * the band they are greyed instead (`renderLines`), so the band's stretch of each curve is the
 * object. Drawn under the curves so the lines stay crisp over it.
 */
const BandCallout: React.VFC<{
  highlight: BandHighlight
  primary: { points: Point[]; color: string }
  against: { points: Point[]; color: string } | null
  scale: AxisScale
  geom: ChartGeometry
}> = ({ highlight, primary, against, scale, geom }) => {
  const { padL, plotW, padT, plotH, labelSize } = geom.plot
  const x0 = freqX(highlight.lo)
  const x1 = freqX(highlight.hi)
  const headSize = labelSize * 1.2
  // The detail line names a gap between two curves, so it is only printed beside a second one.
  const detail = against ? highlight.detail : undefined
  // The sentence sits over the column's centre, but a column at either edge of the plot
  // (bass, treble) would push it past the frame — anchor it to the inner edge instead. The
  // reach is the WIDER of the two lines: the detail is ~60 characters, and sizing to the
  // headline alone let it run out over the dB labels.
  const mid = (x0 + x1) / 2
  const textW =
    Math.max(highlight.text.length * headSize, (detail?.length ?? 0) * labelSize) * CHAR_EM
  let anchor: 'start' | 'middle' | 'end' = 'middle'
  let textX = mid
  let spanL = mid - textW / 2
  if (spanL < padL + 6) {
    anchor = 'start'
    textX = spanL = padL + 6
  } else if (mid + textW / 2 > padL + plotW - 6) {
    anchor = 'end'
    textX = padL + plotW - 6
    spanL = textX - textW
  }

  // Top edge unless a curve runs through it and the bottom edge is clearer: a bass-heavy
  // curve peaks exactly where the bass sentence used to sit.
  const blockH = labelSize * (detail ? 3.4 : 2)
  let minY = Infinity
  let maxY = -Infinity
  for (const c of against ? [primary, against] : [primary]) {
    for (let i = 0; i <= LABEL_PROBES; i++) {
      const hz = freqFromX(Math.min(padL + plotW, spanL + (textW * i) / LABEL_PROBES))
      const y = geom.scaleY(scale, nearestPoint(c.points, hz)[1])
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)
    }
  }
  const roomTop = minY - (padT + blockH)
  const roomBottom = padT + plotH - blockH - maxY
  const atBottom = roomTop < 0 && roomBottom > roomTop
  const headY = atBottom ? padT + plotH - blockH + labelSize * 1.5 : padT + labelSize * 1.5

  return (
    <g aria-hidden="true" pointerEvents="none">
      <rect x={x0} y={padT} width={x1 - x0} height={plotH} className={styles.bandColumn} />
      <text
        x={textX}
        y={headY}
        textAnchor={anchor}
        fontSize={headSize}
        fontWeight={700}
        className={styles.bandText}
      >
        {highlight.text}
      </text>
      {/* Hidden on phones in CSS (`.bandDetail`), not gated here: `plotW` is viewBox user
          units, which say nothing about how many CSS pixels the chart actually got. */}
      {detail && (
        <text
          x={textX}
          y={headY + labelSize * 1.4}
          textAnchor={anchor}
          fontSize={labelSize}
          className={styles.bandDetail}
        >
          {detail}
        </text>
      )}
    </g>
  )
}

const LabChartPanel: React.VFC<LabChartPanelProps> = ({
  productName,
  curves,
  mdaqs,
  mdaqsPending = false,
  primaryColor = LAB_PRIMARY_COLOR,
  overlays,
  active: requested,
  onTabClick,
  geom,
  idPrefix = 'lab',
  toolbar,
  tables = true,
  highlight = null,
  hoverDiff,
  className,
  legend = true,
  blurbs = true,
  tabsFrom = 'primary',
  focus: focusProp,
  onFocusChange,
}) => {
  const measures = measuresDrawn(curves, overlays, tabsFrom)
  const tabs = labTabsFor(measures, mdaqs.length > 0 || mdaqsPending)
  const active = shownTab(tabs, requested)
  const diffOverlay = hoverDiff ? overlays.find(o => o.key === hoverDiff)?.name : undefined
  const attenMeasure = tabMeasure('atten', curves)
  const attenLabel = attenMeasure === 'anc' ? 'ANC' : 'isolation'

  // Overlays drawn on a measure: on or fading out, with curves for it.
  const drawnFor = (mk: LabMeasure): PanelOverlay[] => overlays.filter(o => Boolean(o.curves?.[mk]))
  const hasAtten = (c: LabChartPaths | null | undefined): boolean => Boolean(c?.isolation || c?.anc)
  const attenDrawn = (m: LabMeasure): boolean => Boolean(curves[m]) || drawnFor(m).length > 0

  const attenBlurb = [curves.isolation?.blurb, curves.anc?.blurb, ATTEN_BLURB_TAIL]
    .filter(Boolean)
    .join(' ')

  const freqYTicks = geom.axisTicks(FREQ_SCALE)
  const attenYTicks = geom.axisTicks(ATTEN_SCALE, false)

  // Hover pin: shared cursor state (only one chart is visible at a time), converting the
  // pointer's CSS position back to a frequency via the same log scale that draws the curves.
  // Touch and mouse differ in three places, all of them here rather than in the render tree:
  // touch captures the pointer (so a scrub that wanders off the SVG keeps tracking), survives
  // the lift (the finger was covering the value it just asked for), and only a browser-stolen
  // gesture — a page scroll, which `touch-action: pan-y` deliberately still allows — clears it.
  const [hoverHz, setHoverHz] = useState<number | null>(null)
  // Horizontal geometry only (`PLOT_X`), so this hit-test is the same on both frames.
  const onChartPointer = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * PLOT_X.viewW
    setHoverHz(x >= PLOT_X.padL && x <= PLOT_X.padL + PLOT_X.plotW ? freqFromX(x) : null)
  }, [])
  const onChartDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (e.pointerType !== 'mouse') {
        try {
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch {
          // Capture only widens where a scrub still tracks; it is never what makes the readout
          // work. It throws NotFoundError if the pointer is already gone (a tap so fast the
          // release beat this handler) and Safari has its own history of refusing it on SVG
          // roots — and an exception here would take the `onChartPointer` below down with it,
          // which is precisely the dead-on-tap bug this handler exists to fix.
        }
      }
      onChartPointer(e)
    },
    [onChartPointer]
  )
  // Mouse leaves the chart → the readout goes with it. A touch "leave" is just the finger
  // lifting, which must NOT wipe the numbers the tap was asking for.
  const onChartLeave = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (e.pointerType === 'mouse') {
      setHoverHz(null)
    }
  }, [])
  const onChartCancel = useCallback(() => setHoverHz(null), [])

  // Highlighted legend entries (line keys and attenuation `measureKey`s), shared by both charts.
  // Controlled when the host draws its own key (`focus` / `onFocusChange`).
  const [ownFocus, setOwnFocus] = useState<ReadonlySet<string>>(() => new Set())
  const focus = focusProp ?? ownFocus
  const toggleFocus = (key: string): void => {
    const next = toggleKey(focus, key)
    setOwnFocus(next)
    onFocusChange?.(next)
  }
  // Which lines a chart greys out: with any of ITS lines or measures highlighted, every line not
  // among them. Scoped per chart, so the target highlighted on the frequency tab (or a product
  // with no curve here) doesn't grey the whole attenuation chart.
  const focusOn = (lineKeys: string[], measureKeys: string[]): ChartFocus => {
    const byLine = lineKeys.some(k => focus.has(k))
    const byMeasure = measureKeys.some(k => focus.has(k))
    return {
      item: (k: string): boolean => (lineKeys.includes(k) ? byLine : byMeasure) && !focus.has(k),
      line: (key: string, measure: LabMeasure): boolean =>
        (byLine && !focus.has(key)) || (byMeasure && !focus.has(measureKey(measure))),
    }
  }
  const freqFocus = focusOn(
    [
      ...(curves.frequency ? [PRIMARY_KEY] : []),
      ...(FREQ_TARGET_POINTS ? [TARGET_KEY] : []),
      ...drawnFor('frequency').map(o => o.key),
    ],
    []
  )
  const attenFocus = focusOn(
    [
      ...(hasAtten(curves) ? [PRIMARY_KEY] : []),
      ...overlays.filter(o => hasAtten(o.curves)).map(o => o.key),
    ],
    ATTEN_MEASURES.filter(m => attenDrawn(m.measure)).map(m => measureKey(m.measure))
  )

  // The hoverable curves per chart, in legend order: primary, target (frequency only), overlays.
  // On attenuation each product contributes a series per measure, the `tabMeasure` one first so
  // series[0] — the curve the readout's headline and difference row read — is the primary's.
  const freqSeries = (): HoverSeries[] => {
    const tint = (key: string, color: string): string =>
      paint(color, freqFocus.line(key, 'frequency'))
    const series: HoverSeries[] = curves.frequency
      ? [
          {
            name: productName,
            color: tint(PRIMARY_KEY, primaryColor),
            points: curves.frequency.points,
          },
        ]
      : []
    if (FREQ_TARGET_POINTS) {
      series.push({
        name: FREQ_TARGET_NAME,
        color: tint(TARGET_KEY, LAB_TARGET_COLOR),
        points: FREQ_TARGET_POINTS,
      })
    }
    for (const o of drawnFor('frequency')) {
      const c = o.curves?.frequency
      if (c) {
        series.push({ name: o.name, color: tint(o.key, o.color), points: c.points })
      }
    }
    return series
  }
  const attenOrder =
    attenMeasure === 'anc' ? [ATTEN_MEASURES[1], ATTEN_MEASURES[0]] : ATTEN_MEASURES
  const attenSeries = (): HoverSeries[] => {
    const series: HoverSeries[] = []
    const add = (
      key: string,
      name: string,
      color: string,
      c: LabChartPaths | null | undefined
    ): void => {
      for (const m of attenOrder) {
        const curve = c?.[m.measure]
        if (curve) {
          series.push({
            name: `${name} · ${m.label}`,
            color: paint(color, attenFocus.line(key, m.measure)),
            points: curve.points,
            dashed: m.dashed,
          })
        }
      }
    }
    add(PRIMARY_KEY, productName, primaryColor, curves)
    for (const o of overlays.filter(ov => hasAtten(ov.curves))) {
      add(o.key, o.name, o.color, o.curves)
    }
    return series
  }

  // Overlays carrying MDAQS (and not fading out), for the grouped Audio quality bars.
  const mdaqsOverlays = overlays.filter(o => !o.leaving && o.mdaqs)

  // Legend = every active overlay, flagged for the panel it is drawn in: a solid swatch when
  // that measure's data is available, a muted "· no data" (hollow dot) when it isn't, or "· …"
  // while still loading. Keyed off the panel's own measure rather than `active`, since every
  // panel renders its own legend whether or not it is the visible one.
  // On the two line charts each entry is a toggle: highlighted lines come forward, the rest grey.
  // Once two or more products draw ANC, a product's swatch shows its own ANC dash.
  const renderLegend = (mk: 'frequency' | 'atten' | 'mdaqs'): JSX.Element | null => {
    if (legend === false) {
      return null
    }
    const chart = { frequency: freqFocus, atten: attenFocus, mdaqs: null }[mk]
    const entry = (key: string, swatch: JSX.Element, label: string): JSX.Element => {
      if (!chart) {
        return (
          <span key={key} className={styles.legendItem}>
            {swatch}
            {label}
          </span>
        )
      }
      return (
        <button
          key={key}
          type="button"
          className={clsx(styles.legendItem, styles.legendToggle)}
          aria-pressed={focus.has(key)}
          data-dimmed={chart.item(key) || undefined}
          onClick={() => toggleFocus(key)}
        >
          {swatch}
          {label}
        </button>
      )
    }
    const patterned =
      mk === 'atten' && (curves.anc ? 1 : 0) + drawnFor('anc').filter(o => !o.leaving).length > 1
    const swatch = (color: string, anc: boolean): JSX.Element => {
      if (patterned && anc) {
        return <LineSwatch color={color} dash={ancDash(color)} />
      }
      return <span className={styles.legendLine} style={{ backgroundColor: color }} />
    }
    return (
      <div
        className={styles.legend}
        role={chart ? 'group' : undefined}
        aria-label={chart ? 'Highlight lines' : undefined}
      >
        {entry(PRIMARY_KEY, swatch(primaryColor, Boolean(curves.anc)), productName)}
        {mk === 'frequency' &&
          FREQ_TARGET_POINTS &&
          entry(
            TARGET_KEY,
            <span
              className={styles.legendDash}
              style={{ borderTopColor: LAB_TARGET_COLOR, opacity: TARGET_OPACITY }}
            />,
            FREQ_TARGET_NAME
          )}
        {overlays
          .filter(o => !o.leaving)
          .map(o => {
            let available = Boolean(o.curves?.frequency)
            if (mk === 'mdaqs') {
              available = Boolean(o.mdaqs)
            } else if (mk === 'atten') {
              available = hasAtten(o.curves)
            }
            const loading = mk !== 'mdaqs' && !available && o.curves === undefined
            if (available) {
              return entry(o.key, swatch(o.color, Boolean(o.curves?.anc)), o.name)
            }
            return (
              <span key={o.key} className={`${styles.legendItem} ${styles.legendItemMuted}`}>
                <span className={styles.legendDotHollow} />
                {loading ? `${o.name} · …` : `${o.name} · no data`}
              </span>
            )
          })}
        {mk === 'atten' &&
          ATTEN_MEASURES.filter(m => attenDrawn(m.measure)).map(m =>
            entry(
              measureKey(m.measure),
              <span
                className={m.dashed ? styles.legendDash : styles.legendLine}
                style={
                  m.dashed
                    ? { borderTopColor: 'currentColor' }
                    : { backgroundColor: 'currentColor' }
                }
              />,
              m.key
            )
          )}
      </div>
    )
  }

  /** The band callout on a chart that draws one of `chartMeasures`, if any. */
  const bandFor = (chartMeasures: readonly LabMeasure[]): BandHighlight | null => {
    if (highlight && chartMeasures.includes(highlight.measure)) {
      return highlight
    }
    return null
  }

  /** The band callout on a chart drawing `chartMeasures`; nothing when no band is picked. */
  const calloutFor = (
    chartMeasures: readonly LabMeasure[],
    scale: AxisScale
  ): JSX.Element | null => {
    const banded = bandFor(chartMeasures)
    const main = banded ? curves[banded.measure] : null
    if (!banded || !main) {
      return null
    }
    const mk = banded.measure
    const drawn = drawnFor(mk)
    const target = banded.against ? drawn.find(o => o.key === banded.against) : drawn[0]
    const pts = target?.curves?.[mk]?.points
    return (
      <BandCallout
        highlight={banded}
        primary={{ points: main.points, color: primaryColor }}
        against={target && pts ? { points: pts, color: target.color } : null}
        scale={scale}
        geom={geom}
      />
    )
  }

  /** A measure's curves: the overlays under the primary. ANC is dashed per product. */
  const linesFor = (mk: LabMeasure, scale: AxisScale): ChartLine[] => {
    const dash = (color: string): string | undefined => (mk === 'anc' ? ancDash(color) : undefined)
    const lines = drawnFor(mk).flatMap((o): ChartLine[] => {
      const c = o.curves?.[mk]
      return c
        ? [
            {
              key: o.key,
              measure: mk,
              d: geom.scalePath(c.points, scale),
              color: o.color,
              width: 2.2,
              dash: dash(o.color),
              peer: true,
              leaving: Boolean(o.leaving),
            },
          ]
        : []
    })
    const main = curves[mk]
    if (main) {
      lines.push({
        key: PRIMARY_KEY,
        measure: mk,
        d: geom.scalePath(main.points, scale),
        color: primaryColor,
        width: 2.6,
        dash: dash(primaryColor),
      })
    }
    return lines
  }

  /**
   * Every curve in its fixed order, greyed when another is highlighted. The highlighted ones are
   * drawn again in a layer on top, and their own copy below is hidden, rather than re-ordered:
   * moving an overlay's node would replay its draw-on animation.
   *
   * On top of both, a wide invisible stroke per line makes it clickable, toggling it like its
   * legend entry. Highlighted lines' strokes come last, so they win where lines cross. Mouse only:
   * a tap on the chart is a request for the readout, and a tap near a line shouldn't also
   * highlight it. Touch readers highlight from the legend.
   *
   * With a band callout, every curve is greyed outside it: a greyed copy underneath, and the
   * layers above clipped to the band's column.
   */
  const renderLines = (
    lines: ChartLine[],
    dimmed: (key: string, measure: LabMeasure) => boolean,
    band: { id: string; highlight: BandHighlight | null }
  ): JSX.Element => {
    const live = lines.filter(l => !l.leaving)
    const lit = live.filter(l => !dimmed(l.key, l.measure))
    const raised = lit.length < live.length
    const onLineUp = (key: string) => (e: React.PointerEvent<SVGPathElement>) => {
      if (e.pointerType === 'mouse' && e.button === 0) {
        toggleFocus(key)
      }
    }
    const { padT, plotH } = geom.plot
    const x0 = band.highlight ? freqX(band.highlight.lo) : 0
    return (
      <>
        {band.highlight && (
          <>
            <clipPath id={band.id}>
              <rect x={x0} y={padT} width={freqX(band.highlight.hi) - x0} height={plotH} />
            </clipPath>
            <g>
              {lines.map(l => (
                <ChartPath key={`${l.key}-${l.measure}`} line={l} dimmed />
              ))}
            </g>
          </>
        )}
        <g clipPath={band.highlight ? `url(#${band.id})` : undefined}>
          {lines.map(l => {
            const dim = dimmed(l.key, l.measure)
            return (
              <ChartPath
                key={`${l.key}-${l.measure}`}
                line={l}
                dimmed={dim}
                hidden={raised && !dim && !l.leaving}
              />
            )
          })}
        </g>
        {raised && (
          <g clipPath={band.highlight ? `url(#${band.id})` : undefined}>
            {lit.map(l => (
              <ChartPath key={`${l.key}-${l.measure}`} line={l} dimmed={false} still />
            ))}
          </g>
        )}
        <g aria-hidden="true">
          {[...live.filter(l => !lit.includes(l)), ...lit].map(l => (
            <path
              key={`${l.key}-${l.measure}`}
              d={l.d}
              fill="none"
              stroke="transparent"
              strokeWidth={LINE_HIT_WIDTH}
              pointerEvents="stroke"
              className={styles.lineHit}
              onPointerUp={onLineUp(l.key)}
            />
          ))}
        </g>
      </>
    )
  }

  const chartEvents = {
    onPointerMove: onChartPointer,
    onPointerDown: onChartDown,
    onPointerLeave: onChartLeave,
    onPointerCancel: onChartCancel,
  }

  /**
   * The tab labels are wider than the ~350px a phone gives the strip: the last sits off the
   * right edge, and the one the reader just picked can be the clipped one. The strip scrolls,
   * so keep the ACTIVE tab inside it — on mount (a dialog can open straight onto "Isolation &
   * ANC") and on every change.
   */
  const tabsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const strip = tabsRef.current
    const tab = strip?.querySelector<HTMLElement>('[data-active="true"]') ?? null
    if (!strip || !tab || strip.scrollWidth <= strip.clientWidth) {
      return
    }
    const left = tab.offsetLeft - strip.offsetLeft
    const overshootRight = left + tab.offsetWidth - (strip.scrollLeft + strip.clientWidth)
    const overshootLeft = strip.scrollLeft - left
    if (overshootRight > 0) {
      strip.scrollTo({ left: strip.scrollLeft + overshootRight + 8, behavior: 'smooth' })
    } else if (overshootLeft > 0) {
      strip.scrollTo({ left: Math.max(0, left - 8), behavior: 'smooth' })
    }
  }, [active])

  return (
    <div className={className}>
      <div className={styles.tabs} role="tablist" aria-label="Lab measurements" ref={tabsRef}>
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${t.key}`}
            aria-controls={`${idPrefix}-panel-${t.key}`}
            aria-selected={active === t.key}
            className={styles.tab}
            data-active={active === t.key}
            onClick={() => {
              onTabClick(t)
              // A touch readout persists past the lift; it must not survive into another
              // measurement's panel, where it would state the previous tab's numbers.
              setHoverHz(null)
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {toolbar}

      <div className={styles.panel}>
        {/* All panels stay in the DOM and hide via `hidden`, rather than mounting only the
            active one. The isolation, ANC and MDAQS measurements are first-party SoundGuys
            lab data and the strongest differentiating content on the product page — gating
            them on click state kept most of it out of the server-rendered HTML.
            Tab semantics (tablist/tab/tabpanel) make the same structure correct for screen
            readers. */}
        {measures.has('frequency') && (
          <div
            role="tabpanel"
            id={`${idPrefix}-panel-freq`}
            aria-labelledby={`${idPrefix}-tab-freq`}
            hidden={active !== 'freq'}
          >
            <div className={`${styles.chartFrame} ${styles.chartFrameBleed}`}>
              <ChartTitle
                product={productName}
                descriptor="Frequency Response"
                context={RIG_CONTEXT}
              />
              {renderLegend('frequency')}
              <div className={styles.chartWrap}>
                {/* Before the <svg> in DOM order: on mobile that puts the readout strip above
                    the chart in normal flow; on desktop it is absolutely positioned anyway. */}
                <HoverReadout hz={hoverHz} series={freqSeries()} signed diffWith={diffOverlay} />
                <svg
                  viewBox={geom.viewBox}
                  className={styles.chart}
                  role="img"
                  aria-label={`${productName} frequency response`}
                  aria-describedby={`${idPrefix}-desc-freq`}
                  {...chartEvents}
                >
                  {/* aria-label, NOT a <title> child: <title> makes the browser pop a native
                      tooltip over the hover crosshair; the label reads identically to AT. */}
                  <desc id={`${idPrefix}-desc-freq`}>
                    {curves.frequency?.blurb ?? `${productName} frequency response`}
                  </desc>
                  <ChartAxes yTicks={freqYTicks} yTitle="dB (Relative)" geom={geom} />
                  {calloutFor(['frequency'], FREQ_SCALE)}
                  {renderLines(
                    [
                      ...(FREQ_TARGET_POINTS
                        ? [
                            {
                              key: TARGET_KEY,
                              measure: 'frequency' as const,
                              d: geom.scalePath(FREQ_TARGET_POINTS, FREQ_SCALE),
                              color: LAB_TARGET_COLOR,
                              width: 1.8,
                              dash: TARGET_DASH,
                              opacity: TARGET_OPACITY,
                            },
                          ]
                        : []),
                      ...linesFor('frequency', FREQ_SCALE),
                    ],
                    freqFocus.line,
                    { id: `${idPrefix}-band-freq`, highlight: bandFor(['frequency']) }
                  )}
                  {hoverHz !== null && (
                    <ChartCrosshair
                      hz={hoverHz}
                      series={freqSeries()}
                      scale={FREQ_SCALE}
                      geom={geom}
                    />
                  )}
                </svg>
              </div>
            </div>
            <HighlightHint legend={legend || Boolean(onFocusChange)} />
            {blurbs && (
              <p className={styles.blurb}>
                {FREQ_BLURB_PARTS.before}
                <a href={FREQ_CURVE_LINK_HREF} target="_blank" rel="noopener noreferrer">
                  {FREQ_BLURB_PARTS.linkText}
                </a>
                {FREQ_BLURB_PARTS.after}
              </p>
            )}
            {tables && curves.frequency && (
              <MeasurementTable
                measurement="Frequency response"
                productName={productName}
                points={curves.frequency.points}
                dbColumn="Response (dB)"
              />
            )}
          </div>
        )}

        {(measures.has('isolation') || measures.has('anc')) && (
          <div
            role="tabpanel"
            id={`${idPrefix}-panel-atten`}
            aria-labelledby={`${idPrefix}-tab-atten`}
            hidden={active !== 'atten'}
          >
            <div className={`${styles.chartFrame} ${styles.chartFrameBleed}`}>
              <ChartTitle product={productName} descriptor="Attenuation" context={RIG_CONTEXT} />
              {renderLegend('atten')}
              <div className={styles.chartWrap}>
                <HoverReadout
                  hz={hoverHz}
                  series={attenSeries()}
                  signed={false}
                  diffWith={diffOverlay && `${diffOverlay} · ${attenLabel}`}
                />
                <svg
                  viewBox={geom.viewBox}
                  className={styles.chart}
                  role="img"
                  aria-label={`${productName} isolation and noise cancelling`}
                  aria-describedby={`${idPrefix}-desc-atten`}
                  {...chartEvents}
                >
                  <desc id={`${idPrefix}-desc-atten`}>{attenBlurb}</desc>
                  <ChartAxes yTicks={attenYTicks} yTitle="dB (Attenuation)" geom={geom} />
                  {calloutFor(['isolation', 'anc'], ATTEN_SCALE)}
                  {renderLines(
                    [...linesFor('isolation', ATTEN_SCALE), ...linesFor('anc', ATTEN_SCALE)],
                    attenFocus.line,
                    { id: `${idPrefix}-band-atten`, highlight: bandFor(['isolation', 'anc']) }
                  )}
                  {hoverHz !== null && (
                    <ChartCrosshair
                      hz={hoverHz}
                      series={attenSeries()}
                      scale={ATTEN_SCALE}
                      geom={geom}
                    />
                  )}
                </svg>
              </div>
            </div>
            <HighlightHint legend={legend || Boolean(onFocusChange)} />
            {blurbs && (curves.isolation || curves.anc) && (
              <p className={styles.blurb}>{attenBlurb}</p>
            )}
            {tables && curves.isolation && (
              <MeasurementTable
                measurement="Passive isolation"
                productName={productName}
                points={curves.isolation.points}
                dbColumn="Attenuation (dB)"
              />
            )}
            {tables && curves.anc && (
              <MeasurementTable
                measurement="Noise cancelling"
                productName={productName}
                points={curves.anc.points}
                dbColumn="Total attenuation (dB)"
              />
            )}
          </div>
        )}

        {(mdaqs.length > 0 || mdaqsPending) && (
          <div
            role="tabpanel"
            id={`${idPrefix}-panel-mdaqs`}
            aria-labelledby={`${idPrefix}-tab-mdaqs`}
            hidden={active !== 'mdaqs'}
          >
            {mdaqs.length === 0 ? (
              <>
                {renderLegend('mdaqs')}
                <p className={styles.blurb} aria-live="polite">
                  {'Loading the audio quality scores…'}
                </p>
              </>
            ) : (
              <div className={styles.chartFrame}>
                <ChartTitle product={productName} descriptor="MDAQS" />
                {/* The MDAQS version (the internal tool's "v.2 for Universal") is left out until the
                    scores DealHunt serves and the internal tool's MDAQS rows are reconciled: 41
                    DealHunt products have no internal row and 16 match a row other than the one
                    flagged default (audit 2026-10-19), so no version can be stated reliably. */}
                <p className={styles.chartSubtitle}>
                  {'Multi-Dimensional Audio Quality Scores, Scale 1 - 5'}
                </p>
                {renderLegend('mdaqs')}
                <div className={styles.mosChart}>
                  <span className={styles.mosAxisTitle}>{'Mean opinion score'}</span>
                  <div className={styles.mosTicks} aria-hidden="true">
                    {MOS_TICKS.map(v => (
                      <span key={v} style={{ bottom: `${mosPct(v)}%` }}>
                        {v}
                      </span>
                    ))}
                  </div>
                  {/* Gridlines and bars share this one box, so a bar's top meets its gridline. */}
                  <div className={styles.mosPlot}>
                    {MOS_TICKS.map(v => (
                      <span
                        key={v}
                        className={styles.mosGridline}
                        style={{ bottom: `${mosPct(v)}%` }}
                      />
                    ))}
                    <div className={styles.barGroups}>
                      {MDAQS_METRICS.map(metric => {
                        const primary = mdaqs.find(m => m.key === `mdaqs_${metric.key}`)
                        const bars = [
                          ...(primary
                            ? [{ key: 'primary', score: primary.score, color: primaryColor }]
                            : []),
                          ...mdaqsOverlays.flatMap(o => {
                            const score = o.mdaqs ? o.mdaqs[metric.key] : null
                            return score === null ? [] : [{ key: o.key, score, color: o.color }]
                          }),
                        ]
                        return (
                          <div key={metric.key} className={styles.barCluster}>
                            {bars.map(b => (
                              <div key={b.key} className={styles.barCol}>
                                <span
                                  className={styles.barScore}
                                  style={{ bottom: `${mosPct(b.score)}%` }}
                                >
                                  {b.score.toFixed(1)}
                                </span>
                                <div
                                  className={styles.barFill}
                                  style={{ height: `${mosPct(b.score)}%`, background: b.color }}
                                />
                              </div>
                            ))}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <div className={styles.barLabels}>
                    {MDAQS_METRICS.map(metric => (
                      <span key={metric.key} className={styles.barLabel}>
                        {metric.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {blurbs && (
              <p className={styles.blurb}>
                {'Virtual '}
                <a href={MOS_EXPLAINER_URL} target="_blank" rel="noopener noreferrer">
                  {'mean opinion scores'}
                </a>
                {' (MOS) on a 1–5 scale, modeled from the measured response by HEAD acoustics’ '}
                <a href={MDAQS_EXPLAINER_URL} target="_blank" rel="noopener noreferrer">
                  {'MDAQS'}
                </a>
                {' (Multi-Dimensional Audio Quality Score) model. Higher is better across timbre, '}
                {'distortion, and immersiveness'}
                {toolbar ? ' — add other models to see where this one lands.' : '.'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default LabChartPanel
